/**
 * Display helpers for the Review role: a temporary, read-only seat for an
 * outside reviewer. The server enforces every part of it (read-only, expiry,
 * revocation) on each request; nothing here is a control.
 */
import i18n from 'i18next';

/** The `org_role` value the API uses for a reviewer. */
export const REVIEW_ROLE = 'review';

/** Default and ceiling for a grant, in days. Mirrors the API's 30 / 90. */
export const REVIEW_DEFAULT_DAYS = 30;
export const REVIEW_MAX_DAYS = 90;

/** Durations offered when granting. The API accepts any 1..90. */
export const REVIEW_DURATIONS = [7, 14, 30, 60, 90] as const;

/** "Review", or "Revue" when the UI language is French. */
export function reviewRoleLabel(language?: string): string {
  // `i18n.language` is undefined until i18next is initialised (tests, early
  // render), which the type does not say.
  const lang = (language ?? (i18n.language || 'en')).toLowerCase();
  return lang.startsWith('fr') ? 'Revue' : 'Review';
}

export function isReviewRole(orgRole: string | null | undefined): boolean {
  return orgRole === REVIEW_ROLE;
}

/** A role as shown to people: `review` is capitalised and localised. */
export function roleLabel(role: string, language?: string): string {
  return isReviewRole(role) ? reviewRoleLabel(language) : role;
}

/** "9 Oct 2026", or an em dash for something that is not a date. */
export function formatReviewDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!isFinite(d.valueOf())) return '—';
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * The message to show when a request failed because the reviewer's access
 * ended, or ``null`` for any other failure.
 *
 * The API answers every request of an ended grant with 403 and a ``RV403A*``
 * code (expired, revoked, or area restricted is not one of these). Read off the
 * Axios-style error without importing Axios, as the other Team helpers do.
 */
export function reviewAccessEndedMessage(err: unknown): string | null {
  const data = (err as { response?: { data?: { detail?: unknown } } } | null)
    ?.response?.data;
  const detail = data?.detail as
    { code?: unknown; message?: unknown } | undefined;
  const code = typeof detail?.code === 'string' ? detail.code : '';
  if (code !== 'RV403AE' && code !== 'RV403AR') return null;
  return typeof detail?.message === 'string' && detail.message
    ? detail.message
    : code === 'RV403AE'
      ? 'Your review access has expired.'
      : 'Your review access was revoked.';
}
